import React, { useState, useEffect } from 'react';
import { Bot, Phone, CheckCircle, AlertCircle, Loader, Edit, Trash2, X, Plus, Mic, MessageSquare, PhoneForwarded } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import VoiceSelectionModal from './VoiceSelectionModal';

interface VoiceAgent {
  id: string;
  practice_id: string;
  agent_id: string;
  timezone: string;
  metadata: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  phone_number?: string;
}

const VoiceAgentSection: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<VoiceAgent | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [editedMessage, setEditedMessage] = useState('');
  const [isUpdatingMessage, setIsUpdatingMessage] = useState(false);
  
  // Transfer number state
  const [transferNumber, setTransferNumber] = useState('');
  const [isEditingTransferNumber, setIsEditingTransferNumber] = useState(false);
  const [editedTransferNumber, setEditedTransferNumber] = useState('');
  const [isUpdatingTransferNumber, setIsUpdatingTransferNumber] = useState(false);
  
  // Edit timezone state
  const [showEditTimezoneModal, setShowEditTimezoneModal] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [isUpdatingTimezone, setIsUpdatingTimezone] = useState(false);
  
  // Delete agent state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Voice selection state
  const [currentVoiceId, setCurrentVoiceId] = useState<string>('');
  const [currentVoiceAvatar, setCurrentVoiceAvatar] = useState<string>('');
  const [currentVoiceName, setCurrentVoiceName] = useState<string>('');
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isUpdatingVoice, setIsUpdatingVoice] = useState(false);
  
  // Welcome message modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  
  // Transfer number modal state
  const [showTransferModal, setShowTransferModal] = useState(false);

  // US Timezones
  const US_TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Mountain Time - Arizona (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  ];

  // Format phone number for display (e.g., +14157549214 -> (415) 754-9214)
  const formatPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) return phoneNumber;
    
    // Remove any non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');
    
    // Check if it's a US number (starts with 1 and has 11 digits)
    if (digits.length === 11 && digits.startsWith('1')) {
      const areaCode = digits.substring(1, 4);
      const prefix = digits.substring(4, 7);
      const lineNumber = digits.substring(7, 11);
      return `(${areaCode}) ${prefix}-${lineNumber}`;
    }
    
    // For other formats, return as-is
    return phoneNumber;
  };

  // Convert formatted phone number back to +1... format for API
  const unformatPhoneNumber = (formattedNumber: string): string => {
    if (!formattedNumber) return formattedNumber;
    
    // Remove all non-digit characters
    const digits = formattedNumber.replace(/\D/g, '');
    
    // If it's 10 digits (US number without country code), add +1
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    
    // If it's 11 digits starting with 1, add +
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }
    
    // Otherwise, ensure it starts with +
    return digits.startsWith('+') ? digits : `+${digits}`;
  };

  useEffect(() => {
    if (user?.practice_id) {
      loadVoiceAgent();
    }
  }, [user?.practice_id]);

  const loadVoiceAgent = async () => {
    if (!user?.practice_id) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;

      const response = await fetch(`${baseURL}/api/v1/practices/${user.practice_id}/voice-agent`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 404) {
        // No agent exists - this is expected
        setAgent(null);
      } else if (response.ok) {
        const data = await response.json();
        setAgent(data);
        
        // Load welcome message
        loadWelcomeMessage();
        // Load transfer number
        loadTransferNumber();
        // Load current voice
        loadCurrentVoice();
      } else {
        throw new Error('Failed to load voice agent');
      }
    } catch (err: any) {
      console.error('Error loading voice agent:', err);
      setError(err.message || 'Failed to load voice agent');
    } finally {
      setLoading(false);
    }
  };

  const createVoiceAgent = async () => {
    if (!user?.practice_id) return;

    setCreating(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;

      const response = await fetch(`${baseURL}/api/v1/practices/${user.practice_id}/voice-agent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          timezone: 'US/Pacific',
          metadata: {}
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create voice agent');
      }

      const data = await response.json();
      setAgent(data);
    } catch (err: any) {
      console.error('Error creating voice agent:', err);
      setError(err.message || 'Failed to create voice agent');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const loadWelcomeMessage = async () => {
    if (!user?.practice_id) return;

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;
      
      // URL encode the node name
      const nodeName = encodeURIComponent('Welcome Node');
      const url = `${baseURL}/api/v1/practices/${user.practice_id}/voice-agent/node/${nodeName}/message`;
      
      console.log('🔍 Loading welcome message from:', url);

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Loaded welcome message:', data);
        setWelcomeMessage(data.message);
      } else {
        console.error('❌ Failed to load welcome message, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setWelcomeMessage('Unable to load current message');
      }
    } catch (err) {
      console.error('❌ Error loading welcome message:', err);
      setWelcomeMessage('Unable to load current message');
    }
  };

  const updateWelcomeMessage = async () => {
    if (!user?.practice_id || !editedMessage.trim()) return;

    setIsUpdatingMessage(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;
      
      // URL encode the node name
      const nodeName = encodeURIComponent('Welcome Node');
      const url = `${baseURL}/api/v1/practices/${user.practice_id}/voice-agent/node/${nodeName}/message`;
      
      console.log('🎭 Updating welcome message to:', url);
      console.log('📝 New message preview:', editedMessage.substring(0, 100));

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          personality_text: editedMessage
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to update message, status:', response.status);
        throw new Error(errorData.detail || 'Failed to update message');
      }

      const data = await response.json();
      console.log('✅ Updated welcome message successfully:', data);
      
      // Reload the message from the server to confirm the update
      await loadWelcomeMessage();
      setIsEditingMessage(false);
    } catch (err: any) {
      console.error('❌ Error updating welcome message:', err);
      setError(err.message || 'Failed to update welcome message');
    } finally {
      setIsUpdatingMessage(false);
    }
  };

  const loadTransferNumber = async () => {
    if (!user?.practice_id) return;

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;
      
      // URL encode the node name
      const nodeName = encodeURIComponent('Transfer Call');
      const url = `${baseURL}/api/v1/practices/${user.practice_id}/voice-agent/node/${nodeName}/message`;
      
      console.log('🔍 Loading transfer number from:', url);

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Loaded transfer number:', data);
        setTransferNumber(data.message);
      } else {
        console.error('❌ Failed to load transfer number, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setTransferNumber('Unable to load current number');
      }
    } catch (err) {
      console.error('❌ Error loading transfer number:', err);
      setTransferNumber('Unable to load current number');
    }
  };

  const updateTransferNumber = async () => {
    if (!user?.practice_id || !editedTransferNumber.trim()) return;

    setIsUpdatingTransferNumber(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;
      
      // Convert formatted number back to API format (+1...)
      const apiFormattedNumber = unformatPhoneNumber(editedTransferNumber);
      
      // URL encode the node name
      const nodeName = encodeURIComponent('Transfer Call');
      const url = `${baseURL}/api/v1/practices/${user.practice_id}/voice-agent/node/${nodeName}/message`;
      
      console.log('📞 Updating transfer number to:', url);
      console.log('📝 Formatted input:', editedTransferNumber);
      console.log('📝 API format:', apiFormattedNumber);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          personality_text: apiFormattedNumber
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to update transfer number, status:', response.status);
        console.error('Error details:', errorData);
        throw new Error(errorData.detail || 'Failed to update transfer number');
      }

      console.log('✅ Successfully updated transfer number');
      
      // Reload the number to show updated value
      await loadTransferNumber();
      setIsEditingTransferNumber(false);
    } catch (err: any) {
      console.error('❌ Error updating transfer number:', err);
      setError(err.message || 'Failed to update transfer number');
    } finally {
      setIsUpdatingTransferNumber(false);
    }
  };

  const loadCurrentVoice = async () => {
    if (!user?.practice_id) return;

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;
      
      const url = `${baseURL}/api/v1/practices/${user.practice_id}/voice-agent/voice`;
      
      console.log('🎤 Loading current voice from:', url);

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Loaded current voice:', data);
        setCurrentVoiceId(data.voice_id);
        
        // Load voice details from voice-selection.json
        try {
          const voicesResponse = await fetch('/voice-selection.json');
          const voices = await voicesResponse.json();
          const voiceDetails = voices.find((v: any) => v.voice_id === data.voice_id);
          
          if (voiceDetails) {
            setCurrentVoiceAvatar(voiceDetails.avatar_url);
            setCurrentVoiceName(voiceDetails.voice_name);
            console.log('✅ Loaded voice details:', voiceDetails.voice_name, voiceDetails.avatar_url);
          }
        } catch (err) {
          console.error('❌ Error loading voice details:', err);
        }
      } else {
        console.error('❌ Failed to load current voice, status:', response.status);
      }
    } catch (err) {
      console.error('❌ Error loading current voice:', err);
    }
  };

  const updateVoice = async (voiceId: string) => {
    if (!user?.practice_id) return;

    setIsUpdatingVoice(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;
      
      const url = `${baseURL}/api/v1/practices/${user.practice_id}/voice-agent/voice`;
      
      console.log('🎤 Updating voice to:', voiceId);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          voice_id: voiceId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to update voice, status:', response.status);
        console.error('Error details:', errorData);
        throw new Error(errorData.detail || 'Failed to update voice');
      }

      console.log('✅ Successfully updated voice');
      
      // Update the current voice ID and load details from JSON
      setCurrentVoiceId(voiceId);
      
      // Load voice details from voice-selection.json
      try {
        const voicesResponse = await fetch('/voice-selection.json');
        const voices = await voicesResponse.json();
        const voiceDetails = voices.find((v: any) => v.voice_id === voiceId);
        
        if (voiceDetails) {
          setCurrentVoiceAvatar(voiceDetails.avatar_url);
          setCurrentVoiceName(voiceDetails.voice_name);
          console.log('✅ Updated voice details:', voiceDetails.voice_name, voiceDetails.avatar_url);
        }
      } catch (err) {
        console.error('❌ Error loading voice details:', err);
      }
      
      setShowVoiceModal(false);
    } catch (err: any) {
      console.error('❌ Error updating voice:', err);
      setError(err.message || 'Failed to update voice');
    } finally {
      setIsUpdatingVoice(false);
    }
  };

  const updateTimezone = async () => {
    if (!user?.practice_id || !agent) return;

    setIsUpdatingTimezone(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/practices/${user.practice_id}/voice-agent`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          timezone: selectedTimezone
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update timezone');
      }

      await loadVoiceAgent();
      setShowEditTimezoneModal(false);
    } catch (err: any) {
      console.error('Error updating timezone:', err);
      setError(err.message || 'Failed to update timezone');
    } finally {
      setIsUpdatingTimezone(false);
    }
  };

  const deleteVoiceAgent = async () => {
    if (!user?.practice_id) return;

    setIsDeleting(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/practices/${user.practice_id}/voice-agent`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete voice agent');
      }

      setAgent(null);
      setShowDeleteConfirm(false);
    } catch (err: any) {
      console.error('Error deleting voice agent:', err);
      setError(err.message || 'Failed to delete voice agent');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error && !agent) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={loadVoiceAgent}
              className="mt-3 text-sm font-medium text-red-600 hover:text-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No agent - show setup view
  if (!agent) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12">
        <div className="max-w-md mx-auto text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Bot className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">
              No Voice Agent Configured
            </h3>
            <p className="text-base text-gray-600">
              Set up a voice agent to handle phone calls for your practice. The voice agent will help customers book appointments and answer common questions.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            onClick={createVoiceAgent}
            disabled={creating}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {creating ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Setting up...</span>
              </>
            ) : (
              <>
                <Bot className="w-5 h-5" />
                <span>Set Up Voice Agent</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Agent exists - show configured view
  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Bot className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Voice Agent</h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`px-3 py-1 rounded-full flex items-center space-x-2 ${
              (agent.is_active && agent.phone_number) ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <div className={`w-2 h-2 rounded-full ${(agent.is_active && agent.phone_number) ? 'bg-green-600' : 'bg-gray-600'}`}></div>
              <span className={`text-sm font-medium ${(agent.is_active && agent.phone_number) ? 'text-green-700' : 'text-gray-700'}`}>
                {(agent.is_active && agent.phone_number) ? 'Active' : 'Inactive'}
              </span>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Delete voice agent"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Agent Details Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50 w-1/3">
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>Phone Number</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {agent.phone_number ? (
                    <span>{formatPhoneNumber(agent.phone_number)}</span>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 italic">Not configured</span>
                      <button
                        onClick={() => navigate('/dashboard/phones')}
                        className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        <span>Add</span>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-gray-400" />
                    <span>Timezone</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <div className="flex items-center justify-between">
                    <span>{US_TIMEZONES.find(tz => tz.value === agent.timezone)?.label || agent.timezone}</span>
                    <button
                      onClick={() => {
                        setSelectedTimezone(agent.timezone);
                        setShowEditTimezoneModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                    >
                      <Edit className="w-3 h-3" />
                      <span>Edit</span>
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <Mic className="w-4 h-4 text-gray-400" />
                    <span>Voice</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {currentVoiceAvatar && (
                        <img
                          src={currentVoiceAvatar}
                          alt={currentVoiceName}
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <span>{currentVoiceName || (currentVoiceId ? currentVoiceId.replace('11labs-', '') : 'Loading...')}</span>
                    </div>
                    <button
                      onClick={() => setShowVoiceModal(true)}
                      className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                    >
                      <Edit className="w-3 h-3" />
                      <span>Change</span>
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <MessageSquare className="w-4 h-4 text-gray-400" />
                    <span>Welcome Message</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <div className="flex items-center justify-between">
                    <span className="truncate max-w-md">{welcomeMessage ? (welcomeMessage.length > 50 ? welcomeMessage.substring(0, 50) + '...' : welcomeMessage) : 'Loading...'}</span>
                    <button
                      onClick={() => {
                        setEditedMessage(welcomeMessage);
                        setShowWelcomeModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1 flex-shrink-0 ml-2"
                    >
                      <Edit className="w-3 h-3" />
                      <span>Edit</span>
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <PhoneForwarded className="w-4 h-4 text-gray-400" />
                    <span>Transfer Number</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <div className="flex items-center justify-between">
                    <span>{transferNumber ? formatPhoneNumber(transferNumber) : 'Loading...'}</span>
                    <button
                      onClick={() => {
                        setEditedTransferNumber(formatPhoneNumber(transferNumber));
                        setShowTransferModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                    >
                      <Edit className="w-3 h-3" />
                      <span>Edit</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Welcome Message Modal */}
      {showWelcomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/20" onClick={() => setShowWelcomeModal(false)} />
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 relative z-10">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Welcome Message</h3>
              <button
                onClick={() => setShowWelcomeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Welcome Message
              </label>
              <textarea
                value={editedMessage}
                onChange={(e) => setEditedMessage(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Enter welcome message..."
              />
              <p className="mt-1 text-xs text-gray-500">
                This is the first message callers will hear when they call your practice.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowWelcomeModal(false)}
                disabled={isUpdatingMessage}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateWelcomeMessage();
                  setShowWelcomeModal(false);
                }}
                disabled={isUpdatingMessage || !editedMessage.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUpdatingMessage ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Message'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Number Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/20" onClick={() => setShowTransferModal(false)} />
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative z-10">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Transfer Number</h3>
              <button
                onClick={() => setShowTransferModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="text"
                value={editedTransferNumber}
                onChange={(e) => setEditedTransferNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="(415) 754-9214"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter a US phone number for emergency call transfers. Examples: (415) 754-9214, 415-754-9214, 4157549214
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowTransferModal(false)}
                disabled={isUpdatingTransferNumber}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateTransferNumber();
                  setShowTransferModal(false);
                }}
                disabled={isUpdatingTransferNumber || !editedTransferNumber.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUpdatingTransferNumber ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Number'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Timezone Modal */}
      {showEditTimezoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/20" onClick={() => setShowEditTimezoneModal(false)} />
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative z-10">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Timezone</h3>
              <button
                onClick={() => setShowEditTimezoneModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <select
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {US_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowEditTimezoneModal(false)}
                disabled={isUpdatingTimezone}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={updateTimezone}
                disabled={isUpdatingTimezone}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUpdatingTimezone ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/20" onClick={() => setShowDeleteConfirm(false)} />
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative z-10">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Voice Agent</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Are you sure you want to delete this voice agent? This will deactivate it but won't remove the phone number. You can recreate the agent later if needed.
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={deleteVoiceAgent}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Agent'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Voice Selection Modal */}
      <VoiceSelectionModal
        isOpen={showVoiceModal}
        onClose={() => setShowVoiceModal(false)}
        onSelectVoice={updateVoice}
        currentVoiceId={currentVoiceId}
      />
    </div>
  );
};

export default VoiceAgentSection;

